// "Fill the Gap" — a calm, context-recall practice surface for vocabulary.
//
// We take a vocabulary card whose headword appears verbatim in its own example
// sentence, blank that word out, and ask you to produce it from the English clue
// + the surrounding Spanish. Answers are checked accent-insensitively (so "edad"
// matches whether or not you typed the tilde-free version).
//
// Like Spot the Slip, this is deliberately NOT a game: no score, no streak, no
// timer. After each attempt you reveal the answer and choose "Got it" (drop the
// card) or "Review again" (requeue to the back). Empty queue → a quiet panel.
import { VOCABULARY_CARDS, VOCAB_THEMES, VOCAB_TIERS } from "../data/vocabulary.js";
import { escapeHtml } from "../ui/helpers.js";

const THEME_LABEL = Object.fromEntries(VOCAB_THEMES.map((t) => [t.id, t.label]));
const THEME_EMOJI = Object.fromEntries(VOCAB_THEMES.map((t) => [t.id, t.emoji]));

// Accent/ñ-insensitive normaliser (same shape the conjugation trainer uses).
function normalize(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ñ/g, "n")
    .trim();
}

// Drop a leading article so the blank is just the headword.
function stripArticle(s) {
  return s.replace(/^(el|la|los|las|un|una|unos|unas)\s+/i, "").trim();
}

// Find a whole-word occurrence of `head` in `example` (case-insensitive, accents
// preserved). Returns the split around the match, or null if the word doesn't
// appear verbatim (those cards are simply left out of the deck).
function buildBlank(example, head) {
  const esc = head.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  let re;
  try {
    re = new RegExp(`(^|[^\\p{L}])(${esc})([^\\p{L}]|$)`, "iu");
  } catch {
    return null;
  }
  const m = re.exec(example);
  if (!m) return null;
  const start = m.index + m[1].length;
  const end = start + m[2].length;
  return {
    before: example.slice(0, start),
    answer: example.slice(start, end),
    after: example.slice(end),
  };
}

// Precompute the deck once: cards that yield a clean single-word gap.
// Raw VOCABULARY_CARDS carry no `id` — that field is injected downstream by
// lib/data.js (`local:<spanish>`), which this view bypasses by importing the
// cards directly. Without a unique key the queue/`current()` lookup collapses
// onto card 0, so we key on `rank` (guaranteed unique per card).
const CLOZE_CARDS = VOCABULARY_CARDS.map((c) => {
  const head = stripArticle(c.spanish);
  const blank = buildBlank(c.example_es || "", head);
  return blank ? { ...c, id: c.rank, head, blank } : null;
}).filter(Boolean);

export function clozeView(app, params = {}) {
  let theme = params.theme && THEME_LABEL[params.theme] ? params.theme : "";
  let tier = VOCAB_TIERS.includes(params.tier) ? params.tier : "";
  let queue = [];
  let revealed = false;
  let lastInput = "";

  function pool() {
    return CLOZE_CARDS.filter((c) => {
      if (theme && c.theme !== theme) return false;
      if (tier && c.tier !== tier) return false;
      return true;
    });
  }

  function shuffle(ids) {
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    return ids;
  }

  function resetQueue() {
    queue = shuffle(pool().map((c) => c.id));
    revealed = false;
    lastInput = "";
  }

  function current() {
    return queue.length ? CLOZE_CARDS.find((c) => c.id === queue[0]) : null;
  }

  app.innerHTML = `
    <section>
      <div class="mb-5">
        <h2 class="text-2xl font-bold text-sage-800">Fill the Gap</h2>
        <p class="text-sage-600 text-sm mt-1 max-w-2xl">Produce the missing word from the English clue and the sentence around it. Accents are optional. No scores — reveal when you're ready, then keep it or send it round again.</p>
      </div>
      <div class="flex flex-wrap items-center gap-1.5 mb-3" id="theme-chips">
        <button class="theme-chip" data-theme="">All themes</button>
        ${VOCAB_THEMES.map(
          (t) => `<button class="theme-chip" data-theme="${t.id}">${t.emoji} ${escapeHtml(t.label)}</button>`
        ).join("")}
      </div>
      <div class="flex flex-wrap items-center gap-2 mb-5">
        <span class="text-xs text-sage-500 uppercase tracking-wide mr-1">Tier:</span>
        <button class="tier-chip" data-tier="">All</button>
        ${VOCAB_TIERS.map((t) => `<button class="tier-chip" data-tier="${t}">${t}</button>`).join("")}
      </div>
      <div id="cloze-stage"></div>
    </section>`;

  const stage = app.querySelector("#cloze-stage");

  function paintChips() {
    app.querySelectorAll(".theme-chip").forEach((b) => {
      const on = b.dataset.theme === theme;
      b.className = `theme-chip px-2.5 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
    app.querySelectorAll(".tier-chip").forEach((b) => {
      const on = b.dataset.tier === tier;
      b.className = `tier-chip px-3 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
  }

  function renderCard() {
    const item = current();
    if (!item) {
      renderDone();
      return;
    }
    const remaining = queue.length;
    const gapWidth = Math.max(4, item.head.length + 1);

    stage.innerHTML = `
      <div class="max-w-2xl">
        <div class="flex items-center justify-between mb-3">
          <span class="text-[11px] uppercase tracking-wide text-sage-500">${THEME_EMOJI[item.theme] || ""} ${escapeHtml(THEME_LABEL[item.theme] || item.theme)} · ${escapeHtml(item.tier)}</span>
          <span class="text-xs text-sage-400">${remaining} card${remaining === 1 ? "" : "s"} left</span>
        </div>

        <div class="bg-white border border-sage-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <span class="text-4xl leading-none" aria-hidden="true">${item.emoji || "•"}</span>
            <div>
              <p class="text-xs text-sage-400 uppercase tracking-wide">Clue</p>
              <p class="text-lg font-semibold text-sage-800">${escapeHtml(item.english || "")}</p>
            </div>
          </div>

          <p class="text-lg text-sage-800 leading-relaxed">
            ${escapeHtml(item.blank.before)}<input id="gap" type="text" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
              style="width:${gapWidth}ch"
              class="inline-block align-baseline border-b-2 border-sage-300 focus:border-sage-500 outline-none text-center text-sage-900 bg-sage-50/40 rounded-t px-1 mx-0.5" />${escapeHtml(item.blank.after)}
          </p>

          <div id="reveal" class="hidden mt-5 pt-5 border-t border-sage-100"></div>

          <div id="card-actions" class="mt-5 flex flex-wrap gap-2"></div>
        </div>
      </div>`;

    const input = stage.querySelector("#gap");
    const actions = stage.querySelector("#card-actions");
    const reveal = stage.querySelector("#reveal");

    function showReveal() {
      revealed = true;
      lastInput = input ? input.value : lastInput;
      const correct = normalize(lastInput) === normalize(item.head) && lastInput.trim() !== "";
      const attempted = lastInput.trim() !== "";

      const filled = `${escapeHtml(item.blank.before)}<span class="bg-sage-100 text-sage-800 font-semibold rounded px-1">${escapeHtml(item.blank.answer)}</span>${escapeHtml(item.blank.after)}`;

      const verdict = !attempted
        ? `<span class="text-sage-500">Here's the word:</span>`
        : correct
        ? `<span class="text-sage-700 font-medium">✓ That's it.</span>`
        : `<span class="text-sage-600">You wrote <span class="text-red-600 font-medium">${escapeHtml(lastInput)}</span> — the word is</span>`;

      reveal.innerHTML = `
        <p class="text-sm mb-2">${verdict}</p>
        <p class="text-lg text-sage-800 leading-relaxed">${filled}</p>
        <p class="text-sm text-sage-500 mt-1 italic">${escapeHtml(item.example_en || "")}</p>
        <p class="text-sm text-sage-600 mt-3"><span class="font-semibold text-sage-700">${escapeHtml(item.spanish)}</span> — ${escapeHtml(item.english || "")}</p>`;
      reveal.classList.remove("hidden");
      if (input) input.disabled = true;

      actions.innerHTML = `
        <button id="got-it" class="px-4 py-2 rounded-lg text-sm font-medium bg-sage-500 text-white hover:bg-sage-600 transition">Got it</button>
        <button id="again" class="px-4 py-2 rounded-lg text-sm font-medium bg-white text-sage-600 border border-sage-200 hover:border-sage-300 transition">Review again</button>`;
      actions.querySelector("#got-it").addEventListener("click", () => {
        queue.shift();
        revealed = false;
        lastInput = "";
        renderCard();
      });
      actions.querySelector("#again").addEventListener("click", () => {
        const id = queue.shift();
        queue.push(id);
        revealed = false;
        lastInput = "";
        renderCard();
      });
    }

    if (revealed) {
      if (input) input.value = lastInput;
      showReveal();
    } else {
      actions.innerHTML = `
        <button id="check" class="px-4 py-2 rounded-lg text-sm font-medium bg-sage-500 text-white hover:bg-sage-600 transition">Check</button>
        <button id="show" class="px-4 py-2 rounded-lg text-sm font-medium bg-white text-sage-600 border border-sage-200 hover:border-sage-300 transition">Show answer</button>`;
      actions.querySelector("#check").addEventListener("click", showReveal);
      actions.querySelector("#show").addEventListener("click", () => {
        if (input) input.value = "";
        lastInput = "";
        showReveal();
      });
      if (input) {
        input.focus();
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            showReveal();
          }
        });
      }
    }
  }

  function renderDone() {
    const n = pool().length;
    stage.innerHTML = `
      <div class="max-w-2xl text-center py-12">
        <p class="text-4xl mb-3">🌿</p>
        <p class="text-lg font-semibold text-sage-800">All caught up</p>
        <p class="text-sage-600 text-sm mt-1">You worked through ${n} gap${n === 1 ? "" : "s"}${theme ? " in " + escapeHtml(THEME_LABEL[theme]) : ""}${tier ? " · " + escapeHtml(tier) : ""}. Repetition is the whole point — come back any time.</p>
        <button id="restart" class="mt-5 px-4 py-2 rounded-lg text-sm font-medium bg-white text-sage-600 border border-sage-200 hover:border-sage-300 transition">Shuffle and go again</button>
      </div>`;
    stage.querySelector("#restart").addEventListener("click", () => {
      resetQueue();
      renderCard();
    });
  }

  app.querySelectorAll(".theme-chip").forEach((b) =>
    b.addEventListener("click", () => {
      theme = b.dataset.theme;
      paintChips();
      resetQueue();
      renderCard();
    })
  );
  app.querySelectorAll(".tier-chip").forEach((b) =>
    b.addEventListener("click", () => {
      tier = b.dataset.tier;
      paintChips();
      resetQueue();
      renderCard();
    })
  );

  paintChips();
  resetQueue();
  renderCard();
}
